// §15 / D-043 — the macOS sound set: sparse, bell-like, and generated.
//
// Nothing here is a file and nothing is sampled from macOS: these are oscillators and envelopes
// arrived at from scratch, in the shape the platform's sounds have — a soft glass strike rather
// than house's warm two-tone or Windows' brighter chime.
//
// It lands with the tokens rather than with the components (D-144): `tests/arch/sounds.test.ts`
// treats a skin *directory* as the unit, so a directory with tokens and no sound set is a red
// tree for as long as it takes to build the rest of the skin.
import type { SoundId, SoundRecipe } from '@hangar/shared';

export const macosSounds: Record<SoundId, SoundRecipe> = {
  // A struck bell: one attack, one high partial above it, both decaying together.
  notify: {
    tones: [
      { wave: 'sine', freq: 1174, at: 0, duration: 0.14, gain: 0.11, attack: 0.002, release: 0.12 },
      { wave: 'sine', freq: 1760, at: 0.005, duration: 0.11, gain: 0.05, attack: 0.002, release: 0.1 },
    ],
  },
  // Low and short. macOS says "no" quietly, and twice is one time too many.
  error: {
    tones: [
      { wave: 'sine', freq: 262, at: 0, duration: 0.11, gain: 0.16, attack: 0.003, release: 0.07 },
      { wave: 'sine', freq: 196, at: 0.09, duration: 0.15, gain: 0.14, release: 0.11 },
    ],
  },
  // §5.4 makes this audible often, so it is the shortest thing here: a tick, not a tone.
  snap: {
    tones: [{ wave: 'sine', freq: 880, endFreq: 1174, at: 0, duration: 0.05, gain: 0.07, release: 0.03 }],
  },
  // The crumple: a falling band of noise-ish sawtooth, gone in under a fifth of a second.
  trash: {
    tones: [
      { wave: 'sawtooth', freq: 520, endFreq: 130, at: 0, duration: 0.16, gain: 0.08, release: 0.11 },
      { wave: 'triangle', freq: 320, endFreq: 110, at: 0.02, duration: 0.13, gain: 0.05, release: 0.09 },
    ],
  },
  // A shutter: two clicks, close together, with nothing musical about either.
  screenshot: {
    tones: [
      { wave: 'square', freq: 1800, at: 0, duration: 0.02, gain: 0.06, release: 0.012 },
      { wave: 'square', freq: 1200, at: 0.045, duration: 0.03, gain: 0.05, release: 0.02 },
    ],
  },
  deviceConnect: {
    tones: [
      { wave: 'sine', freq: 659, at: 0, duration: 0.08, gain: 0.1, attack: 0.004, release: 0.05 },
      { wave: 'sine', freq: 988, at: 0.075, duration: 0.12, gain: 0.09, attack: 0.004, release: 0.08 },
    ],
  },
  // The chord: a wide, slow major triad that arrives all at once and takes its time leaving.
  startup: {
    tones: [
      { wave: 'sine', freq: 262, at: 0, duration: 0.9, gain: 0.07, attack: 0.09, release: 0.6 },
      { wave: 'sine', freq: 392, at: 0.02, duration: 0.88, gain: 0.06, attack: 0.09, release: 0.6 },
      { wave: 'sine', freq: 523, at: 0.04, duration: 0.86, gain: 0.05, attack: 0.09, release: 0.6 },
      { wave: 'sine', freq: 784, at: 0.06, duration: 0.84, gain: 0.035, attack: 0.09, release: 0.6 },
    ],
  },
};
